/**
 * Shared hover zoom for `.blog-post-card` images.
 * Works with event delegation so dynamically mounted cards are covered.
 */

/**
 * @param {ParentNode} root
 * @returns {{ destroy: () => void }}
 */
export function initBlogPostCardZoom(root = document) {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /** @type {WeakMap<HTMLElement, HTMLElement>} */
  const cardImages = new WeakMap();
  const abort = new AbortController();
  const { signal } = abort;

  const cards = root.querySelectorAll('.blog-post-card');
  cards.forEach((card) => {
    const el = /** @type {HTMLElement} */ (card);
    const img = /** @type {HTMLElement | null} */ (el.querySelector('.blog-post-card__image'));
    const container = /** @type {HTMLElement | null} */ (
      el.querySelector('.blog-post-card__image-container')
    );
    if (!img || !container) return;

    container.style.overflow = 'hidden';
    img.style.transformOrigin = 'center center';
    img.style.transition = reduceMotion ? 'none' : 'transform 0.45s ease';
    cardImages.set(el, img);
  });

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  const cardFromEvent = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return null;
    const card = target.closest('.blog-post-card');
    if (!(card instanceof HTMLElement)) return null;
    if (root instanceof Element && !root.contains(card)) return null;
    if (root instanceof Document && !root.contains(card)) return null;
    return card;
  };

  /**
   * @param {HTMLElement} card
   * @returns {HTMLElement | null}
   */
  const imageFor = (card) => cardImages.get(card) || card.querySelector('.blog-post-card__image');

  if (!reduceMotion) {
    const listenerRoot = root instanceof Document ? root.body : root;

    listenerRoot.addEventListener(
      'pointerover',
      (event) => {
        const card = cardFromEvent(event);
        if (!card || card.classList.contains('is-zooming')) return;
        const img = imageFor(card);
        if (!img) return;
        card.classList.add('is-zooming');
        img.style.setProperty('transform', 'scale(1.06)');
      },
      { signal }
    );

    listenerRoot.addEventListener(
      'pointerout',
      (event) => {
        const card = cardFromEvent(event);
        if (!card || !card.classList.contains('is-zooming')) return;
        const related = /** @type {Node | null} */ (event.relatedTarget);
        if (related && card.contains(related)) return;
        const img = imageFor(card);
        card.classList.remove('is-zooming');
        img?.style.removeProperty('transform');
      },
      { signal }
    );
  }

  return {
    destroy() {
      abort.abort();
    },
  };
}

class BlogPostCardZoom extends HTMLElement {
  /** @type {{ destroy: () => void } | null} */
  #controller = null;

  connectedCallback() {
    requestAnimationFrame(() => {
      this.#controller?.destroy();
      this.#controller = initBlogPostCardZoom(this);
    });
  }

  disconnectedCallback() {
    this.#controller?.destroy();
    this.#controller = null;
  }
}

if (!customElements.get('blog-post-card-zoom')) {
  customElements.define('blog-post-card-zoom', BlogPostCardZoom);
}

/**
 * Auto-bind sections that load this file as a script entry
 * (educational / featured-blog-posts). `blog-posts-list` inits itself.
 */
function autoInitStandaloneSections() {
  document.querySelectorAll('.educational-section__cards, .featured-blog-posts').forEach((root) => {
    if (root.closest('blog-posts-list')) return;
    initBlogPostCardZoom(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInitStandaloneSections, { once: true });
} else {
  autoInitStandaloneSections();
}
